import type { CalculationResult } from '#calculator/Calculator'

import { nonAdjustableExpenses } from './spendLessLever'
import { sumActiveIncome } from './sumActiveIncome'
import type { ChartBaselines } from './useAdjustments'

/**
 * Derives the What-If slider baselines from calculator results.
 *
 * Uses the result for `currentYear` (defaults to today) rather than
 * `results[0]`, which is the earliest *record* year and may predate active
 * income/expense records by years, causing sliders to incorrectly not appear.
 *
 * Falls back to `results[0]` when no result exists for `currentYear`.
 */
export const computeBaselines = (
  results: readonly CalculationResult[],
  retirementAge: number,
  currentYear = new Date().getFullYear()
): ChartBaselines => {
  const baselineYear = results.find(r => r.year === currentYear) ?? results[0]
  // Round so stepper increment+decrement land back at ratio=0 exactly;
  // a fractional baseline (e.g. 83998.27) would leave a tiny epsilon ratio
  // after a round-trip and "No change" would never return.
  return {
    retirementAge,
    income: Math.round(sumActiveIncome(baselineYear)),
    // The whole line, because that is what a user recognises as their
    // spending. What the lever may not drag it below travels beside it.
    expenses: Math.round(baselineYear.aggregatedData.expenses.totalValue),
    nonAdjustableExpenses: Math.round(
      nonAdjustableExpenses(baselineYear.aggregatedData)
    ),
  }
}
